// @flow

import _ from 'lodash';
import { FullStory, init } from '@fullstory/browser';
import queryString from 'query-string';
import moment from 'moment';

import { integrations } from '../config';
import { SemanticSearch, ProductPerformanceRanking, Personalization } from 'src/models/search';
import { enableShopifyIntegration } from 'src/helpers/theme-setup-helper';
import { ELEMENT_KEY_NAME, ELEMENTS } from 'src/constants/theme-settings';
import { MENU_ID } from 'src/constants/theme-settings.jsx';

const marketTypeMapping = {
  default: 'Store default',
  country: 'Specific country',
  company: 'Specific company location'
};

const getBoostPlan = (tenantApp: Object): string => {
  return _.get(tenantApp, 'selectedPlan', '').replace('_discount', '') || 'Trial';
};

const buildDataEventEditISW = (data: FilterSettingsType): Record<String, any> => {
  const suggestionBlocks = _.get(data, 'search.suggestionBlocks', []);
  const productsBlock = suggestionBlocks.find(
    suggestionBlock => suggestionBlock.type === 'products'
  );
  const collectionsBlock = suggestionBlocks.find(
    suggestionBlock => suggestionBlock.type === 'collections'
  );
  const suggestionsBlock = suggestionBlocks.find(
    suggestionBlock => suggestionBlock.type === 'suggestions'
  );
  const pagesBlock = suggestionBlocks.find(suggestionBlock => suggestionBlock.type === 'pages');

  return {
    disProduct: !!(_.get(productsBlock, 'status', 'active') === 'active'),
    disCollection: !!(_.get(collectionsBlock, 'status', 'active') === 'active'),
    disPopularSuggestions: !!(_.get(suggestionsBlock, 'status', 'active') === 'active'),
    disBlogAndPage: !!(_.get(pagesBlock, 'status', 'active') === 'active'),
    disSearchTermSuggest: _.get(data, 'search.suggestionNoResult.search_terms.status', false),
    disPopularProducts: _.get(data, 'search.suggestionNoResult.products.status', false),
    disRecentSearches: _.get(data, 'search.searchBoxOnclick.recentSearch.status', true),
    disSearchTermSuggestInSearchBox: _.get(
      data,
      'search.searchBoxOnclick.searchTermSuggestion.status',
      false
    ),
    disPopularProductsInSearchBox: _.get(
      data,
      'search.searchBoxOnclick.productSuggestion.status',
      false
    )
  };
};

const flattenObj = ob => {
  let result = {};

  for (const i in ob) {
    if (typeof ob[i] === 'object' && !Array.isArray(ob[i])) {
      const temp = flattenObj(ob[i]);
      for (const j in temp) {
        result[i + '.' + j] = temp[j];
      }
    } else {
      result[i] = ob[i];
    }
  }
  return result;
};

const expandObj = (target, keySeparator) => {
  var result = {};
  for (var key in target) {
    if (Object.prototype.hasOwnProperty.call(target, key)) {
      var nestedKeys = key.split(keySeparator);
      // Get the last subKey
      var leaf = nestedKeys[nestedKeys.length - 1];
      // Get all subKeys except for the last
      var branch = nestedKeys.slice(0, nestedKeys.length - 1);

      var currentTarget = result;
      for (var i = 0; i < branch.length; i += 1) {
        var subKey = nestedKeys[i];
        // If this is the first time visiting this branch, we need to instantiate it
        if (currentTarget[subKey] === undefined) {
          currentTarget[subKey] = {};
        }
        // Visit the branch
        currentTarget = currentTarget[subKey];
      }
      currentTarget[leaf] = target[key];
    }
  }
  return result;
};

// Init Full Story
const initFullStory = () => {
  const queries = queryString.parse(window.location.search);
  // If having auth0Token => access via app simulator
  const { auth0Token, shop = '' } = queries;
  const isSimulator = auth0Token ? true : false;

  init({
    orgId: integrations.fullStory.orgId,
    debug: integrations.fullStory.debug || isSimulator,
    devMode: integrations.fullStory.devMode,
    recordCrossDomainIFrames: true,
    recordOnlyThisIFrame: true
  });

  if (shop) {
    FullStory('setIdentity', {
      uid: shop
    });
  }
};

/**
 * Set user property from tenant
 */
const setupFullStoryUser = tenantApp => {
  const plan = getBoostPlan(tenantApp);
  const isEnableShopifyIntegration = enableShopifyIntegration(tenantApp);
  const firstInstalledAt = _.get(tenantApp, 'firstInstalledAt');
  const recentInstalledAt = _.get(tenantApp, 'recentInstalledAt');
  let userProperty = {
    displayName: tenantApp.externalInfo?.name,
    tenantId: tenantApp.tenantId,
    boostPlan: plan,
    shopifyPlan: _.get(tenantApp, 'externalInfo.plan_display_name', ''),
    productCount: tenantApp.appParams?.currentTotalProducts,
    name: tenantApp.externalInfo?.name,
    language: tenantApp.appLanguage,
    country: tenantApp.externalInfo?.country_name,
    devStore: _.startsWith(tenantApp.tenantId, 'bc-dev-v2'),
    ssrEnable: isEnableShopifyIntegration,
    subscriptionV4Enable: _.get(tenantApp, 'settings.subscriptionV4.settings.enable', false),
    exceededPlan: _.get(tenantApp, 'exceededPlan', false),
    firstInstalledAt: firstInstalledAt
      ? moment(Number(firstInstalledAt) * 1000).format('YYYY-MM-DD hh:mm:ss A')
      : '',
    recentInstalledAt: recentInstalledAt
      ? moment(Number(recentInstalledAt) * 1000).format('YYYY-MM-DD hh:mm:ss A')
      : ''
  };

  // Check active tenant
  if (plan === 'Trial') {
    const trialExpiresAt = _.get(tenantApp, 'trialExpiresAt', 0);
    userProperty.trialEndDate = trialExpiresAt
      ? moment.utc(trialExpiresAt).format('YYYY-MM-DD hh:mm:ss A')
      : '';
    userProperty.trialExpired = _.get(tenantApp, 'trialExpired', false);
  }

  FullStory('setIdentity', {
    uid: tenantApp.tenantId,
    properties: userProperty
  });
};

/**
 * Track event
 */
const trackFSEvent = (name, properties) => {
  console.log('trackFSEvent()', name, properties);
  FullStory('trackEvent', { name, properties });
};

/**
 * Track specific events
 */
const trackFSEvent_UpdateFilterSettings = (type: String, data: Record<String, any>): void => {
  if (type === 'filterLayout') {
    trackFSEvent('Change Filter Layout', {
      feature: 'Filter Layout',
      verticalLayout: data?.general?.filterTreeVerticalStyle,
      horizontalLayout: data?.general?.filterTreeHorizontalStyle,
      mobileLayout: data?.general?.filterTreeMobileStyle,
      stickyOnMobile: data?.general?.stickyFilterOnMobile,
      stickyOnDesktop: data?.general?.stickyFilterOnDesktop
    });
  }
  if (type === 'filterSettings') {
    trackFSEvent('Change Filter Settings', {
      feature: 'Filter Settings'
    });
  }
  if (type === 'instantSearchDisplay') {
    const layoutSettingsMapping = {
      style1: 'One Column',
      style2: 'Default - Two Columns',
      style3: 'Overlay Fullwidth'
    };
    // Tracking when enable toggle instant search
    if (data.isEnableSuggestionTracking) {
      trackFSEvent('Enable Instant Search ', {
        feature: 'Instant Search Display',
        layoutSettings: layoutSettingsMapping[data?.suggestionStyle]
      });
      return;
    }
    // Tracking when edit instant search
    if (data?.search?.enableSuggestion) {
      const dataTracking = buildDataEventEditISW(data);
      trackFSEvent('Edit Instant Search Display', {
        feature: 'Instant Search Display',
        ...dataTracking
      });
    } else {
      // Tracking when disabled instant search
      trackFSEvent('Disable Instant Search', {
        feature: 'Instant Search Display',
        layoutSettings: layoutSettingsMapping[data?.search?.suggestionStyle]
      });
    }
  }
  if (type === 'searchPageDisplay') {
    trackFSEvent('Edit Search Results Page', {
      feature: 'Search Page Display',
      disResultCollection: data?.search?.searchPanelBlocks?.collections?.active,
      disResultPages: data?.search?.searchPanelBlocks?.pages?.active,
      disSearchTips: data?.search?.searchPanelBlocks?.searchTips?.active,
      disSearchTermSuggest: data?.search?.searchPanelBlocks?.searchTermSuggestions?.active,
      disPopularProducts: data?.search?.searchPanelBlocks?.mostPopularProducts?.active
    });
  }
  if (type === 'translation') {
    trackFSEvent('Change Translation', {
      feature: 'Translation'
    });
  }
};

const trackFSEvent_SelectAnalyticsFrame = () => {
  const path = window.location.pathname;
  const pathNames = path.substring(1).split('/');
  trackFSEvent('Select Analytics Frame', {
    feature: 'Analytics',
    analyticsType: pathNames?.[0],
    subType: pathNames?.[1] || 'overview'
  });
};

const trackFSEvent_UpdateRelevanceSearchSetting = (
  currentSearchSetting: SearchSetting,
  newSearchSetting: SearchSetting
) => {
  // Search fields settings
  const currentSearchFields = currentSearchSetting.searchFields.sort((a, b) => a.name - b.name);
  const newSearchFields = newSearchSetting.searchFields.sort((a, b) => a.name - b.name);
  for (let i = 0; i < currentSearchFields.length; i++) {
    if (currentSearchFields[i].status !== newSearchFields[i].status) {
      const trackingSearchField = {};
      newSearchFields.forEach(e => {
        trackingSearchField[e.name] = e?.searchWeight || 'non-searchable field';
      });
      trackFSEvent('Edit Relevance Settings', {
        feature: 'Standard Search',
        ...trackingSearchField
      });
      break;
    }
  }
};

const trackFSEvent_SemanticSearch = (oldSettings: SemanticSearch, settings: SemanticSearch) => {
  if (oldSettings?.enable === false && settings?.enable === true) {
    trackFSEvent('Enable Semantic Search', {
      feature: 'Semantic Search'
    });
  }

  if (oldSettings?.enable === true && settings?.enable === false) {
    trackFSEvent('Disable Semantic Search', {
      feature: 'Semantic Search'
    });
  }
};

const trackFSEvent_ProductPerformanceRanking = (
  oldSettings: ProductPerformanceRanking,
  settings: ProductPerformanceRanking
) => {
  if (oldSettings?.enabled !== true && settings?.enabled === true) {
    trackFSEvent('Enable Product Performance Ranking', {
      feature: 'Product performance ranking'
    });
  }

  if (oldSettings?.enabled !== false && settings?.enabled === false) {
    trackFSEvent('Disable Product Performance Ranking', {
      feature: 'Product performance ranking'
    });
  }

  const { numberReordered = 0 } = oldSettings;
  const { numberReordered: newNumberReordered = 0 } = settings;
  if (parseInt(numberReordered) !== parseInt(newNumberReordered)) {
    trackFSEvent('Adjust Product Number Ranking', {
      feature: 'Product performance ranking',
      productLimits: settings?.numberReordered
    });
  }
};

const trackFSEvent_Personalization = (oldSettings: Personalization, settings: Personalization) => {
  if (oldSettings?.search.enabled !== true && settings?.search.enabled === true) {
    trackFSEvent('Enable Search Personalization', {
      feature: 'Personalization'
    });
  }

  if (oldSettings?.search.enabled !== false && settings?.search.enabled === false) {
    trackFSEvent('Disable Search Personalization', {
      feature: 'Personalization'
    });
  }

  const { numberReordered = 0 } = oldSettings.search;
  const { numberReordered: newNumberReordered = 0 } = settings.search;
  if (parseInt(numberReordered) !== parseInt(newNumberReordered)) {
    trackFSEvent('Adjust Search Personalized Items Limitations', {
      feature: 'Personalization',
      productLimits: settings?.search.numberReordered
    });
  }

  if (oldSettings?.collection.enabled !== true && settings?.collection.enabled === true) {
    trackFSEvent('Enable Collection Personalization', {
      feature: 'Personalization'
    });
  }

  if (oldSettings?.collection.enabled !== false && settings?.collection.enabled === false) {
    trackFSEvent('Disable Collection Personalization', {
      feature: 'Personalization'
    });
  }

  const { numberReordered: numberReorderedCollection = 0 } = oldSettings.collection;
  const { numberReordered: newNumberReorderedCollection = 0 } = settings.collection;
  if (parseInt(numberReorderedCollection) !== parseInt(newNumberReorderedCollection)) {
    trackFSEvent('Adjust Collection Personalized Items Limitations', {
      feature: 'Personalization',
      productLimits: settings?.collection.numberReordered
    });
  }
};

/**
 * Track merchandising operations (create, update, delete, etc.)
 * @param {string} eventName - Event name to track
 * @param {Object} rule - Merchandising rule object
 */
const trackFSEvent_Merchandising = (eventName: string, rule: Object) => {
  try {
    if (!rule) return;

    const { type: marketType = 'default' } = rule.market || {};
    const strategies = rule?.strategies.map(strategy => strategy.type) || [];
    let attributes = rule?.strategies.map(strategy => strategy.attributes?.map(e => e.attribute));
    attributes = Array.from(new Set(_.flatten(attributes))).filter(i => !!i);

    const data = {
      feature: 'Merchandising V2',
      pageType: rule.ruleType,
      selectedMarket: marketTypeMapping[marketType] || 'Store default',
      strategies,
      attributes,
      numberStrategies: strategies.length,
      numberAttributes: attributes.length
    };

    if (_.has(rule, 'isDisabledPerformanceRanking')) {
      _.set(data, 'isDisabledPerformanceRanking', rule?.isDisabledPerformanceRanking);
    }

    trackFSEvent(eventName, data);
  } catch (error) {
    console.warn('Error tracking Full Story event', error);
  }
};

const trackFSEvent_ChangeThemeElement = (
  oldSetting: Record<String, any>,
  newSetting: Record<String, any>,
  themeName: string,
  addedElements: Record<String, any>[],
  removedElements: Record<String, any>[]
): void => {
  const flattenOldSetting = flattenObj(oldSetting);
  const flattenNewSetting = flattenObj(newSetting);
  const flattenResult = {};

  for (const key of Object.keys(flattenNewSetting)) {
    if (_.isNil(flattenOldSetting[key])) {
      flattenResult[key] = { type: 'new', value: flattenNewSetting[key] };
      continue;
    }

    if (flattenOldSetting[key] !== flattenNewSetting[key]) {
      flattenResult[key] = {
        type: 'change',
        from: flattenOldSetting[key],
        to: flattenNewSetting[key]
      };
    }
  }

  for (const key of Object.keys(flattenOldSetting)) {
    if (_.isNil(flattenNewSetting[key])) {
      flattenResult[key] = { type: 'remove', value: flattenOldSetting[key] };
    }
  }
  const expandResult = expandObj(flattenResult, '.');
  for (const key of Object.keys(expandResult)) {
    // check element is already created event
    let element = _.find(addedElements, item => item.elementName === key) || {};
    if (_.isEmpty(element))
      element = _.find(removedElements, item => item.elementName === key) || {};

    if (_.isEmpty(element)) {
      trackFSEvent('Change element settings', {
        feature: 'Visual Editor',
        elementName: key,
        selectedTheme: themeName,
        dataChange: expandResult[key]
      });
    }
  }
};

const trackFSEvent_ChangeSSRVisualEditor = (
  selectedTemplate: string,
  templateId: string,
  settings: Object
): void => {
  const { colors = {}, typography = {} } = settings;

  const baseColors = [
    `baseColor1 - ${colors['baseColor1'] || ''}`,
    `baseColor2 - ${colors['baseColor2'] || ''}`,
    `baseColor3 - ${colors['baseColor3'] || ''}`,
    `baseColor4 - ${colors['baseColor4'] || ''}`,
    `baseColor5 - ${colors['baseColor5'] || ''}`
  ];
  const accentColors = [
    `accentColor1 - ${colors['accentColor1'] || ''}`,
    `accentColor2 - ${colors['accentColor2'] || ''}`,
    `accentColor3 - ${colors['accentColor3'] || ''}`,
    `accentColor4 - ${colors['accentColor4'] || ''}`
  ];
  const typographyFont = [
    `heading - ${_.get(typography, 'headings.fontFamily', '')}`,
    `body - ${_.get(typography, 'body.fontFamily', '')}`,
    `buttons - ${_.get(typography, 'buttons.fontFamily', '')}`,
    `linkAccent - ${_.get(typography, 'linkAccent.fontFamily', '')}`
  ];

  const { setting: valueSetting } = settings;

  const trackingData = {
    productAdditionalInformation: [],
    collectionElements: [],
    toolbarElements: []
  };
  const {
    productLabel,
    productInformation_productPrice,
    productInformation_productVendor,
    productInformation_rating,
    collectionHeader_setting,
    productImage_setting
  } = valueSetting;

  // Product label
  const MapText = {
    showSaleLabel: 'Show Sale label',
    showSoldOutLabel: 'Show Sold out label',
    showCustomByTagLabel: 'Show Custom by tag label'
  };
  trackingData.productLabelElements = ['showSaleLabel', 'showSoldOutLabel', 'showCustomByTagLabel']
    .filter(key => Boolean(productLabel[key]))
    .map(key => MapText[key]);

  // Price
  trackingData.enableProductPrice = productInformation_productPrice.isEnable;

  // Swatch
  const keySwatch1 = Object.keys(valueSetting).find(key => key.includes('swatches-1'));
  const keySwatch2 = Object.keys(valueSetting).find(key => key.includes('swatches-2'));
  const keySwatch3 = Object.keys(valueSetting).find(key => key.includes('swatches-3'));
  trackingData.swatchShowOnHovering = _.get(valueSetting, `swatches.showOptionValueOnHovering`);
  trackingData.enable1stSwatchDisplay = _.get(valueSetting, `${keySwatch1}.isEnable`);
  trackingData.enable2ndSwatchDisplay = _.get(valueSetting, `${keySwatch2}.isEnable`);
  trackingData.enable3rdSwatchDisplay = _.get(valueSetting, `${keySwatch3}.isEnable`);

  // Button
  const keyAddToCartBtn = Object.keys(valueSetting).find(key =>
    key.includes(ELEMENT_KEY_NAME.ADD_TO_CART_BUTTON)
  );
  const keyQvViewBtn = Object.keys(valueSetting).find(key =>
    key.includes(ELEMENT_KEY_NAME.QUICK_VIEW_BUTTON)
  );
  const addToCartBtn = _.get(valueSetting, keyAddToCartBtn);
  const qvBtn = _.get(valueSetting, keyQvViewBtn);
  trackingData.enableAddToCartBtn = addToCartBtn.isEnable;
  trackingData.enableQuickViewBtn = qvBtn.isEnable;

  // Additional information
  if (productInformation_productVendor.isEnable) {
    trackingData.productAdditionalInformation.push('Product vendor');
  }
  if (productInformation_rating.isEnable) {
    trackingData.productAdditionalInformation.push('Product rating');
  }

  // Product list
  trackingData.layoutType = _.get(productImage_setting, 'aspectRatioType');

  // Collection header
  trackingData.enableCollectionHeader = _.get(collectionHeader_setting, 'isEnable');
  if (_.get(valueSetting, `${ELEMENTS.COLLECTION_HEADER_PRODUCT_COUNT}.isEnable`)) {
    trackingData.collectionElements.push('Collection header Product count');
  }
  if (_.get(valueSetting, `${ELEMENTS.COLLECTION_HEADER_DESCRIPTION}.isEnable`)) {
    trackingData.collectionElements.push('Collection header Description');
  }
  if (_.get(valueSetting, `${ELEMENTS.COLLECTION_HEADER_IMAGE}.isEnable`)) {
    trackingData.collectionElements.push('Collection header Image');
  }

  // if (_.get(valueSetting, `${ELEMENTS.COLLECTION_HEADER_BREADCRUMBS}.isEnable`) &&
  //   !_.get(originalValueSetting, `${ELEMENTS.COLLECTION_HEADER_BREADCRUMBS}.isEnable`)) {
  //   trackingProductListData.enableCollectionHeaderBreadcrumbs = _.get(collectionHeader_setting, 'isEnable')
  // }

  // Toolbar
  if (_.get(valueSetting, `${ELEMENTS.TOOLBAR_PRODUCT_COUNT}.isEnable`)) {
    trackingData.toolbarElements.push('Toolbar Product count');
  }
  if (_.get(valueSetting, `${ELEMENTS.TOOLBAR_VIEW_AS}.isEnable`)) {
    trackingData.toolbarElements.push('Toolbar View as');
  }
  if (_.get(valueSetting, `${ELEMENTS.TOOLBAR_SHOW_LIMIT}.isEnable`)) {
    trackingData.toolbarElements.push('Toolbar Show limit');
  }
  if (_.get(valueSetting, `${ELEMENTS.TOOLBAR_SORT_BY}.isEnable`)) {
    trackingData.toolbarElements.push('Toolbar Sort by');
  }

  // Pagination
  trackingData.paginationType = _.get(valueSetting, `${MENU_ID.PAGINATION}.paginationType`);
  trackingData.paginationAlignment = _.get(valueSetting, `${MENU_ID.PAGINATION}.alignment`);
  trackingData.paginationShowProductCount = _.get(
    valueSetting,
    `${MENU_ID.PAGINATION}.productCount.showProductCount`
  );
  trackingData.paginationProductCountPosition = _.get(
    valueSetting,
    `${MENU_ID.PAGINATION}.productCount.position`
  );

  // Advanced settings
  trackingData.enableProductImageInQuickViewPopup = _.get(
    valueSetting,
    `${MENU_ID.QUICK_VIEW}.showProductImage`
  );
  trackingData.enableBuyItNowButton = _.get(
    valueSetting,
    `${MENU_ID.QUICK_VIEW}.buyItNowBtn.enable`
  );
  trackingData.enableAjaxCart = _.get(valueSetting, `${MENU_ID.AJAX_CART}.enableCart`);
  trackingData.typeOfCardDisplay = _.get(valueSetting, `${MENU_ID.AJAX_CART}.cartStyle`);

  // Smart Styling
  trackingData.sourceTheme = _.get(settings, `smartStyling.selectedThemeName`);
  trackingData.sourceStyle = _.get(settings, `smartStyling.sourceStyle`); // 'url' or 'theme' or undefined

  // Tracking settings
  trackFSEvent('Edit Visual Editor', {
    feature: 'SSR Visual Editor',
    selectedTemplate,
    templateId,
    baseColors,
    accentColors,
    typography: typographyFont,
    ...trackingData
  });
};

const trackFSEvent_ChangeCodeEditor = (
  selectedTemplate: string,
  templateId: string,
  editedWidget: Object
): void => {
  trackFSEvent('Edit code editor', {
    feature: 'SSR Code Editor',
    selectedTemplate,
    templateId,
    widgetName: editedWidget.widgetName
  });
};
export {
  initFullStory,
  setupFullStoryUser,
  trackFSEvent,
  trackFSEvent_UpdateFilterSettings,
  trackFSEvent_SelectAnalyticsFrame,
  trackFSEvent_UpdateRelevanceSearchSetting,
  trackFSEvent_SemanticSearch,
  trackFSEvent_ProductPerformanceRanking,
  trackFSEvent_Personalization,
  trackFSEvent_Merchandising,
  trackFSEvent_ChangeThemeElement,
  trackFSEvent_ChangeSSRVisualEditor,
  trackFSEvent_ChangeCodeEditor
};
